import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Infinity, User, Mail, X } from "lucide-react";
import { useEffect, useState } from "react";
import SacredGeometry from "@/components/SacredGeometry";
import { useTranslation } from "@/hooks/useTranslation";
import DisclaimerModal from "@/components/DisclaimerModal";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RelationshipResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stars, setStars] = useState([]);
  const [selectedAxis, setSelectedAxis] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const result = location.state?.result;

  useEffect(() => {
    if (!result) {
      navigate("/relationship");
      return;
    }

    const generateStars = () => {
      const newStars = [];
      for (let i = 0; i < 80; i++) {
        newStars.push({
          id: i,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          delay: `${Math.random() * 3}s`
        });
      }
      setStars(newStars);
    };
    generateStars();
  }, [result, navigate]);

  if (!result) return null;

  const axes = [
    { key: "mental", data: result.axe_mental, label: t('axis_mental'), color: "blue" },
    { key: "ame", data: result.axe_ame, label: t('axis_soul'), color: "purple" },
    { key: "manifestation", data: result.axe_manifestation, label: t('axis_manifestation'), color: "green" }
  ];

  const diagonals = showAdvanced && result.diagonale_montante ? [
    { key: "diag_montante", data: result.diagonale_montante, label: t('diag_montante'), color: "orange" },
    { key: "diag_descendante", data: result.diagonale_descendante, label: t('diag_descendante'), color: "pink" },
    { key: "diag_petite_1", data: result.petite_diagonale_1, label: t('diag_petite_1'), color: "teal" },
    { key: "diag_petite_2", data: result.petite_diagonale_2, label: t('diag_petite_2'), color: "cyan" }
  ] : [];

  const handleContactRequest = () => {
    navigate("/contact", {
      state: {
        readingData: result,
        readingType: "relationship"
      }
    });
  };

  const handleCellClick = async (frequency, plan, personName) => {
    try {
      const response = await axios.get(`${API}/interpretation/${frequency}/${plan}`);
      setSelectedCell({
        frequency,
        plan,
        personName,
        interpretation: response.data
      });
    } catch (error) {
      console.error("Error fetching interpretation:", error);
    }
  };

  const scrollToAxisSection = (axisKey) => {
    // Find and set the selected axis
    const axis = axes.find(a => a.key === axisKey);
    if (axis) {
      setSelectedAxis(axis);
      // Wait for the section to render, then scroll to it
      setTimeout(() => {
        const section = document.querySelector('[data-testid="axis-interpretation"]');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const scrollToPointNodal = () => {
    // Scroll to Point Nodal section
    setTimeout(() => {
      const section = document.querySelector('[data-testid="point-nodal-section"]');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen relative pb-12">
      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <DisclaimerModal onAccept={() => setShowDisclaimer(false)} />
      )}
      <div className="stars-container">
        {stars.map((star) => (
          <div
            key={star.id}
            className="star"
            style={{
              left: star.left,
              top: star.top,
              animationDelay: star.delay
            }}
          />
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <Button
            data-testid="back-button"
            onClick={() => navigate("/relationship")}
            variant="outline"
            className="border-white/20 bg-white/5 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <img
            src="/elua-logo.png"
            alt="ELUA"
            className="h-16 w-16 object-contain logo-pulse"
          />
        </div>

        <div className="text-center mb-12" data-testid="results-header">
          <div className="inline-flex items-center justify-center mb-4">
            <Infinity className="w-10 h-10 golden-accent" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 glow-text" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('title_square')}
          </h1>
        </div>

        {/* Instruction Banner */}
        <div className="mb-6 bg-gradient-to-r from-[#F4C430]/20 to-[#D4AF37]/20 border border-[#F4C430]/50 rounded-lg p-4 animate-pulse-slow">
          <div className="flex items-center justify-center gap-3 text-[#F4C430]">
            <span className="text-2xl">💡</span>
            <p className="text-base sm:text-lg font-semibold text-white">
              Cliquez sur les cases du tableau pour voir les interprétations détaillées
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card className="glass-card border golden-border">
            <CardContent className="p-4 text-center">
              <User className="w-6 h-6 text-[#F4C430] mx-auto mb-2" />
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                {result.person1.name}
              </h3>
              <p className="text-white/70 text-sm">{result.person1.date}</p>
            </CardContent>
          </Card>
          <Card className="glass-card border golden-border">
            <CardContent className="p-4 text-center">
              <User className="w-6 h-6 text-[#F4C430] mx-auto mb-2" />
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                {result.person2.name}
              </h3>
              <p className="text-white/70 text-sm">{result.person2.date}</p>
            </CardContent>
          </Card>
        </div>

        {/* Vue d'ensemble - Carré 3x3 ELUA */}
        <Card className="glass-card border-2 golden-border mb-8" data-testid="overview-square">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl text-white text-center px-4" style={{ fontFamily: 'Cinzel, serif' }}>
              📦 Vue d'Ensemble — {t('square_title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full border-collapse min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-[#F4C430]/50">
                    <th className="p-2 sm:p-3 text-white/60 text-xs sm:text-sm font-normal"></th>
                    <th className="p-2 sm:p-3 text-[#F4C430] font-semibold text-xs sm:text-base" style={{ fontFamily: 'Cinzel, serif' }}>
                      {t('mental')}
                    </th>
                    <th className="p-2 sm:p-3 text-[#F4C430] font-semibold text-xs sm:text-base" style={{ fontFamily: 'Cinzel, serif' }}>
                      {t('soul')}
                    </th>
                    <th className="p-2 sm:p-3 text-[#F4C430] font-semibold text-xs sm:text-base" style={{ fontFamily: 'Cinzel, serif' }}>
                      {t('manifestation')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Personne 1 */}
                  <tr className="border-b border-white/10">
                    <td className="p-2 sm:p-3 text-white/80 font-semibold text-xs sm:text-base" style={{ fontFamily: 'Cinzel, serif' }}>
                      {result.person1.name}
                    </td>
                    <td className="p-2 sm:p-3 text-center">
                      <div 
                        onClick={() => handleCellClick(result.square_3x3.a_mental, 'mental', result.person1.name)}
                        className="bg-white/10 rounded-lg p-2 sm:p-3 border border-white/20 cursor-pointer hover:bg-white/20 hover:border-[#F4C430]/50 transition-all h-20 sm:h-24 flex items-center justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-1 right-1 text-[#F4C430] opacity-0 group-hover:opacity-100 transition-opacity text-sm">👆</span>
                        <p className="text-2xl sm:text-2xl sm:text-3xl font-bold text-white">{result.square_3x3.a_mental}</p>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div 
                        onClick={() => handleCellClick(result.square_3x3.a_ame, 'ame', result.person1.name)}
                        className="bg-white/10 rounded-lg p-2 sm:p-3 border border-white/20 cursor-pointer hover:bg-white/20 hover:border-[#F4C430]/50 transition-all h-20 sm:h-24 flex items-center justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-1 right-1 text-[#F4C430] opacity-0 group-hover:opacity-100 transition-opacity text-sm">👆</span>
                        <p className="text-2xl sm:text-3xl font-bold text-white">{result.square_3x3.a_ame}</p>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div 
                        onClick={() => handleCellClick(result.square_3x3.a_manifestation, 'manifestation', result.person1.name)}
                        className="bg-white/10 rounded-lg p-2 sm:p-3 border border-white/20 cursor-pointer hover:bg-white/20 hover:border-[#F4C430]/50 transition-all h-20 sm:h-24 flex items-center justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-1 right-1 text-[#F4C430] opacity-0 group-hover:opacity-100 transition-opacity text-sm">👆</span>
                        <p className="text-2xl sm:text-3xl font-bold text-white">{result.square_3x3.a_manifestation}</p>
                      </div>
                    </td>
                  </tr>
                  {/* Lien / Point Nodal */}
                  <tr className="border-b border-white/10 bg-[#F4C430]/5">
                    <td className="p-3 text-[#F4C430] font-semibold" style={{ fontFamily: 'Cinzel, serif' }}>
                      Lien
                    </td>
                    <td className="p-3 text-center">
                      <div 
                        onClick={() => scrollToAxisSection('mental')}
                        className="bg-[#F4C430]/20 rounded-lg p-3 border border-[#F4C430]/50 cursor-pointer hover:bg-[#F4C430]/30 hover:border-[#F4C430] transition-all h-24 flex flex-col justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-2 right-2 text-[#F4C430] opacity-50 group-hover:opacity-100 transition-opacity">🔍</span>
                        <p className="text-xs text-[#F4C430] font-semibold mb-1">Axe Mental</p>
                        <p className="text-2xl sm:text-3xl font-bold text-white">{result.axe_mental.frequency}</p>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div 
                        onClick={scrollToPointNodal}
                        className="bg-gradient-to-br from-[#D4AF37] to-[#F4C430] rounded-lg p-3 border-2 border-[#F4C430] shadow-lg cursor-pointer hover:shadow-2xl hover:scale-105 transition-all h-24 flex flex-col justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-2 right-2 text-[#0a2e38] opacity-50 group-hover:opacity-100 transition-opacity">🔍</span>
                        <p className="text-xs text-[#0a2e38] font-semibold mb-1">Point Nodal</p>
                        <p className="text-3xl font-bold text-[#0a2e38]">{result.square_3x3.point_nodal}</p>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div 
                        onClick={() => scrollToAxisSection('manifestation')}
                        className="bg-[#F4C430]/20 rounded-lg p-3 border border-[#F4C430]/50 cursor-pointer hover:bg-[#F4C430]/30 hover:border-[#F4C430] transition-all h-24 flex flex-col justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-2 right-2 text-[#F4C430] opacity-50 group-hover:opacity-100 transition-opacity">🔍</span>
                        <p className="text-xs text-[#F4C430] font-semibold mb-1">Axe Manifestation</p>
                        <p className="text-2xl sm:text-3xl font-bold text-white">{result.axe_manifestation.frequency}</p>
                      </div>
                    </td>
                  </tr>
                  {/* Personne 2 */}
                  <tr>
                    <td className="p-3 text-white/80 font-semibold" style={{ fontFamily: 'Cinzel, serif' }}>
                      {result.person2.name}
                    </td>
                    <td className="p-3 text-center">
                      <div 
                        onClick={() => handleCellClick(result.square_3x3.b_mental, 'mental', result.person2.name)}
                        className="bg-white/10 rounded-lg p-2 sm:p-3 border border-white/20 cursor-pointer hover:bg-white/20 hover:border-[#F4C430]/50 transition-all h-20 sm:h-24 flex items-center justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-1 right-1 text-[#F4C430] opacity-0 group-hover:opacity-100 transition-opacity text-sm">👆</span>
                        <p className="text-2xl sm:text-3xl font-bold text-white">{result.square_3x3.b_mental}</p>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div 
                        onClick={() => handleCellClick(result.square_3x3.b_ame, 'ame', result.person2.name)}
                        className="bg-white/10 rounded-lg p-2 sm:p-3 border border-white/20 cursor-pointer hover:bg-white/20 hover:border-[#F4C430]/50 transition-all h-20 sm:h-24 flex items-center justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-1 right-1 text-[#F4C430] opacity-0 group-hover:opacity-100 transition-opacity text-sm">👆</span>
                        <p className="text-2xl sm:text-3xl font-bold text-white">{result.square_3x3.b_ame}</p>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div 
                        onClick={() => handleCellClick(result.square_3x3.b_manifestation, 'manifestation', result.person2.name)}
                        className="bg-white/10 rounded-lg p-2 sm:p-3 border border-white/20 cursor-pointer hover:bg-white/20 hover:border-[#F4C430]/50 transition-all h-20 sm:h-24 flex items-center justify-center group relative"
                        title="Cliquer pour voir l'interprétation"
                      >
                        <span className="absolute top-1 right-1 text-[#F4C430] opacity-0 group-hover:opacity-100 transition-opacity text-sm">👆</span>
                        <p className="text-2xl sm:text-3xl font-bold text-white">{result.square_3x3.b_manifestation}</p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-2 golden-border mb-8 card-entrance" data-testid="square-3x3">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              {t('square_title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
              <div className="aspect-square bg-white/10 border-2 border-[#F4C430]/50 rounded-lg flex flex-col items-center justify-center">
                <p className="text-xs text-white/60 mb-1">{t('mental')}</p>
                <SacredGeometry frequency={result.square_3x3.a_mental} size={50} className="text-[#F4C430]" />
                <p className="text-2xl font-bold text-white mt-2">{result.square_3x3.a_mental}</p>
              </div>
              <div className="aspect-square bg-white/10 border-2 border-[#F4C430]/50 rounded-lg flex flex-col items-center justify-center">
                <p className="text-xs text-white/60 mb-1">{t('soul')}</p>
                <SacredGeometry frequency={result.square_3x3.a_ame} size={50} className="text-[#F4C430]" />
                <p className="text-2xl font-bold text-white mt-2">{result.square_3x3.a_ame}</p>
              </div>
              <div className="aspect-square bg-white/10 border-2 border-[#F4C430]/50 rounded-lg flex flex-col items-center justify-center">
                <p className="text-xs text-white/60 mb-1">{t('manifestation')}</p>
                <SacredGeometry frequency={result.square_3x3.a_manifestation} size={50} className="text-[#F4C430]" />
                <p className="text-2xl font-bold text-white mt-2">{result.square_3x3.a_manifestation}</p>
              </div>

              <div className="col-span-3 flex justify-center">
                <div className="w-32 h-32 bg-gradient-to-r from-[#D4AF37] to-[#F4C430] border-4 border-white/20 rounded-full flex flex-col items-center justify-center shadow-2xl">
                  <p className="text-xs text-[#0a2e38]/70 font-semibold mb-1">Point Nodal</p>
                  <SacredGeometry frequency={result.square_3x3.point_nodal} size={50} className="text-[#0a2e38]" />
                  <p className="text-3xl font-bold text-[#0a2e38] mt-1">{result.square_3x3.point_nodal}</p>
                </div>
              </div>

              <div className="aspect-square bg-white/10 border-2 border-[#F4C430]/50 rounded-lg flex flex-col items-center justify-center">
                <p className="text-xs text-white/60 mb-1">{t('mental')}</p>
                <SacredGeometry frequency={result.square_3x3.b_mental} size={50} className="text-[#F4C430]" />
                <p className="text-2xl font-bold text-white mt-2">{result.square_3x3.b_mental}</p>
              </div>
              <div className="aspect-square bg-white/10 border-2 border-[#F4C430]/50 rounded-lg flex flex-col items-center justify-center">
                <p className="text-xs text-white/60 mb-1">{t('soul')}</p>
                <SacredGeometry frequency={result.square_3x3.b_ame} size={50} className="text-[#F4C430]" />
                <p className="text-2xl font-bold text-white mt-2">{result.square_3x3.b_ame}</p>
              </div>
              <div className="aspect-square bg-white/10 border-2 border-[#F4C430]/50 rounded-lg flex flex-col items-center justify-center">
                <p className="text-xs text-white/60 mb-1">{t('manifestation')}</p>
                <SacredGeometry frequency={result.square_3x3.b_manifestation} size={50} className="text-[#F4C430]" />
                <p className="text-2xl font-bold text-white mt-2">{result.square_3x3.b_manifestation}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-2 border-[#F4C430] mb-8" data-testid="point-nodal-section">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              {t('nodal_point')}
            </CardTitle>
            <p className="text-[#F4C430] text-center text-lg font-semibold">
              {t('frequency')} {result.point_nodal.frequency}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-[#F4C430]/10 rounded-lg p-4 border border-[#F4C430]/30">
              <p className="text-white/90 italic">{result.point_nodal.signature}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-2">✅ {t('aligned')}</h4>
              <p className="text-white/80">{result.point_nodal.aligned}</p>
            </div>
            <Separator className="bg-white/10" />
            <div>
              <h4 className="text-white font-semibold mb-2">⚠️ {t('non_aligned')}</h4>
              <p className="text-white/80">{result.point_nodal.non_aligned}</p>
            </div>
            <Separator className="bg-white/10" />
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-blue-300 font-semibold mb-2">📚 {t('teaching')}</h4>
              <p className="text-white/90 font-medium">{result.point_nodal.teaching}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-2 golden-border mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-white text-center" style={{ fontFamily: 'Cinzel, serif' }}>
              {t('explore_square')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {axes.map((axis) => (
                <div key={axis.key} className="flex flex-col items-center gap-2">
                  <div className="text-center">
                    <p className="text-[#F4C430] text-2xl font-bold mb-1">{axis.data.frequency}</p>
                    <p className="text-white/60 text-xs">{t('frequency')}</p>
                  </div>
                  <Button
                    data-testid={`explore-${axis.key}-button`}
                    onClick={() => setSelectedAxis(axis)}
                    className="w-full bg-white/5 hover:bg-white/10 border border-[#F4C430]/50 text-white"
                  >
                    {axis.label}
                  </Button>
                </div>
              ))}
            </div>

            {/* Diagonals section removed */}
          </CardContent>
        </Card>

        {selectedAxis && (
          <Card className="glass-card border-2 border-[#F4C430] mb-8 card-entrance" data-testid="axis-interpretation">
            <CardHeader>
              <CardTitle className="text-2xl text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                {selectedAxis.data.name}
              </CardTitle>
              <p className="text-[#F4C430] text-lg font-semibold">
                Fréquence {selectedAxis.data.frequency}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#F4C430]/10 rounded-lg p-4 border border-[#F4C430]/30">
                <p className="text-white/90 italic">{selectedAxis.data.signature}</p>
              </div>
              
              {/* Show full interpretation for Axes (aligned/non-aligned) */}
              {selectedAxis.data.aligned && (
                <>
                  <div>
                    <h4 className="text-white font-semibold mb-2">✅ {t('poles_aligned')}</h4>
                    <p className="text-white/80">{selectedAxis.data.aligned}</p>
                  </div>
                  <Separator className="bg-white/10" />
                  <div>
                    <h4 className="text-white font-semibold mb-2">⚠️ {t('poles_non_aligned')}</h4>
                    <p className="text-white/80">{selectedAxis.data.non_aligned}</p>
                  </div>
                  <Separator className="bg-white/10" />
                </>
              )}
              
              {/* Teaching always shown */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="text-blue-300 font-semibold mb-2">📚 {selectedAxis.data.aligned ? t('teaching_link') : t('teaching')}</h4>
                <p className="text-white/90 font-medium">{selectedAxis.data.teaching}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="glass-card border-2 border-[#F4C430] mt-12">
          <CardContent className="p-8 text-center">
            <Mail className="w-12 h-12 text-[#F4C430] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Cinzel, serif' }}>
              {t('deep_reading_title')}
            </h3>
            <p className="text-white/80 mb-4 max-w-2xl mx-auto">
              {t('deep_reading_desc')}
            </p>
            
            {/* Diagonals Information Box */}
            <div className="bg-white/5 border border-[#F4C430]/30 rounded-lg p-6 mb-6 max-w-3xl mx-auto">
              <h4 className="text-[#F4C430] font-semibold mb-3 text-lg" style={{ fontFamily: 'Cinzel, serif' }}>
                ✨ Les Diagonales du Carré Relationnel
              </h4>
              <p className="text-white/90 mb-3 leading-relaxed">
                Au-delà des <strong>3 axes fondamentaux</strong> (Mental, Âme, Manifestation) et du <strong>Point Nodal</strong>, 
                le carré relationnel révèle également <strong>4 diagonales</strong> qui offrent une compréhension plus fine 
                et nuancée de votre lien :
              </p>
              <div className="grid md:grid-cols-2 gap-3 text-left text-sm mb-4">
                <div className="bg-white/5 rounded p-3">
                  <p className="text-[#F4C430] font-semibold mb-1">📐 Diagonale Montante</p>
                  <p className="text-white/70">Les dynamiques d'élévation de la relation</p>
                </div>
                <div className="bg-white/5 rounded p-3">
                  <p className="text-[#F4C430] font-semibold mb-1">📐 Diagonale Descendante</p>
                  <p className="text-white/70">Les ancrages et manifestations concrètes</p>
                </div>
                <div className="bg-white/5 rounded p-3">
                  <p className="text-[#F4C430] font-semibold mb-1">💫 Petite Diagonale 1</p>
                  <p className="text-white/70">Le cheminement émotionnel de {result.person1.name}</p>
                </div>
                <div className="bg-white/5 rounded p-3">
                  <p className="text-[#F4C430] font-semibold mb-1">💫 Petite Diagonale 2</p>
                  <p className="text-white/70">Le cheminement émotionnel de {result.person2.name}</p>
                </div>
              </div>
              <p className="text-white/80 italic">
                Ces diagonales apportent des <strong>clés de lecture subtiles</strong> qui enrichissent la compréhension 
                des dynamiques relationnelles et permettent d'identifier les <strong>zones de friction</strong> ou de 
                <strong> synergie</strong> entre les deux personnes.
              </p>
            </div>

            <Button
              data-testid="contact-cta-button"
              onClick={handleContactRequest}
              className="bg-gradient-to-r from-[#D4AF37] to-[#F4C430] hover:from-[#F4C430] hover:to-[#D4AF37] text-[#0a2e38] font-semibold px-8 py-6 text-lg"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              <Mail className="w-5 h-5 mr-2" />
              {t('btn_request_reading')}
            </Button>
          </CardContent>
        </Card>

        <div className="text-center mt-8 p-6 bg-white/5 rounded-lg border border-[#F4C430]/30">
          <p className="text-white/70 text-sm italic whitespace-pre-line">
            « {t('disclaimer_square')} »
          </p>
          <p className="text-[#F4C430] mt-4 font-semibold" style={{ fontFamily: 'Cinzel, serif' }}>
            {t('signature_elua')} ∞
          </p>
        </div>
      </div>

      {/* Modal for Cell Interpretation */}
      {selectedCell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <Card className="glass-card border-2 golden-border max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <CardHeader className="relative">
              <Button
                onClick={() => setSelectedCell(null)}
                variant="ghost"
                className="absolute top-4 right-4 text-white hover:text-[#F4C430]"
              >
                <X className="w-5 h-5" />
              </Button>
              <CardTitle className="text-2xl text-white text-center pr-10" style={{ fontFamily: 'Cinzel, serif' }}>
                {selectedCell.personName} — {selectedCell.plan === 'mental' ? t('mental') : selectedCell.plan === 'ame' ? t('soul') : t('manifestation')}
              </CardTitle>
              <div className="text-center mt-2">
                <span className="inline-block bg-[#F4C430] text-[#0a2e38] px-4 py-2 rounded-full font-bold text-xl">
                  {t('frequency')} {selectedCell.frequency}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <span className="text-2xl">✅</span> {t('aligned')}
                </h4>
                <p className="text-white/80">{selectedCell.interpretation.aligned}</p>
              </div>
              <Separator className="bg-white/10" />
              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                  <span className="text-2xl">⚠️</span> {t('non_aligned')}
                </h4>
                <p className="text-white/80">{selectedCell.interpretation.non_aligned}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RelationshipResults;
